import type { CSSObject } from '@mui/material/styles';

/**
 * Generates a border gradient CSS object.
 *
 * @param {BorderGradientProps} props - The properties for the border gradient.
 * @param {string} [props.color] - The gradient color.
 * @param {string} [props.padding='2px'] - The padding inside the border.
 * @returns {CSSObject} The CSS object for the border gradient.
 *
 * @example
 * ...theme.mixins.borderGradient({ color: `to right, ${theme.vars.palette.primary.main}, ${alpha(theme.vars.palette.primary.mainChannel, 0.2)}`, padding: '4px' }),
 */
export type BorderGradientProps = {
    color?: string;
    padding?: string;
};

/**
 * Creates a CSS object representing a gradient border effect.
 *
 * This function uses CSS masking techniques to create a border effect with customizable color and thickness.
 * The technique allows for positioning a gradient as a border around an element while preserving the border-radius.
 *
 * @param props - Optional configuration properties for the border gradient
 * @param props.color - The color or gradient to use for the border
 * @param props.padding - The thickness of the border (defaults to '2px')
 * @returns A CSS object that can be applied to create a gradient border effect
 *
 * @example
 * ```tsx
 * const styledComponent = styled.div`
 *   position: relative;
 *   border-radius: 8px;
 *
 *   &::before {
 *     ${borderGradient({ color: 'linear-gradient(to right, #f00, #00f)', padding: '3px' })}
 *   }
 * `;
 * ```
 */
export function borderGradient(props?: BorderGradientProps): CSSObject {
    const { color, padding = '2px' } = props ?? {};

    return {
        padding,
        inset: 0,
        width: '100%',
        content: '""',
        height: '100%',
        margin: 'auto',
        position: 'absolute',
        borderRadius: 'inherit',
        mask: 'linear-gradient(#FFF 0 0) content-box, linear-gradient(#FFF 0 0)',
        WebkitMask: 'linear-gradient(#FFF 0 0) content-box, linear-gradient(#FFF 0 0)',
        maskComposite: 'exclude',
        WebkitMaskComposite: 'xor',
        ...(color && {
            background: color,
        }),
    };
}
